import { CloseRounded, LogoutRounded, PersonRounded } from "@mui/icons-material";
import {
  Avatar,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  drawerClasses,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import HeaderClock from "@/components/HeaderClock";
import LanguageSelector from "@/components/LanguageSelector";
import { ROUTES } from "@/constants";
import { useTranslation } from "@/context/I18nProvider";
import ColorModeIcon from "@/layout/Header/ColorModeIcon";
import { logoutUser } from "@/services";
import { dispatch, logout, useSelector } from "@/store";
import { getMediaUrl } from "@/utils";

import MenuContent from "./MenuContent";

interface SidebarMobileProps {
  open: boolean | undefined;
  toggleDrawer: (newOpen: boolean) => () => void;
}

export default function SidebarMobile({ open, toggleDrawer }: SidebarMobileProps) {
  const { t } = useTranslation();
  const user = useSelector((state) => state.auth.user);
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logoutUser();
    } catch {
      // Server-side cleanup failed (network blip, already-expired session).
      // Continue with local logout - leaving the user signed in would be worse.
    }
    dispatch(logout());
  };

  const goToProfile = () => {
    if (!user?._id) return;
    navigate(ROUTES.USER_PROFILE, { state: { userId: user._id } });
    toggleDrawer(false)();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={toggleDrawer(false)}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        [`& .${drawerClasses.paper}`]: {
          backgroundImage: "none",
          backgroundColor: "background.paper",
        },
      }}
    >
      <Stack
        sx={{
          width: "100dvw",
          height: "100%",
        }}
      >
        <Stack direction="row" sx={{ p: 2, pb: 1, gap: 1 }}>
          <Stack direction="row" sx={{ gap: 1, alignItems: "center", flexGrow: 1, p: 1 }}>
            <Avatar
              sizes="small"
              alt={user?.username || t("common.user")}
              src={getMediaUrl(user?.avatarUrl) || undefined}
              sx={{ width: 24, height: 24 }}
            />
            <Typography component="p" variant="h6" noWrap>
              {user?.username || t("common.user")}
            </Typography>
          </Stack>
          <IconButton size="small" aria-label={t("common.close")} onClick={toggleDrawer(false)}>
            <CloseRounded />
          </IconButton>
        </Stack>
        <Divider />
        <Stack sx={{ flexGrow: 1, overflowY: "auto" }}>
          <MenuContent onNavigate={toggleDrawer(false)} />
          <Divider />
        </Stack>
        <Divider />
        <List>
          <ListItem disablePadding>
            <ListItemButton onClick={goToProfile} disabled={!user?._id}>
              <ListItemIcon>
                <PersonRounded />
              </ListItemIcon>
              <ListItemText primary={t("common.profile")} />
            </ListItemButton>
          </ListItem>
        </List>
        <Divider />
        <Stack sx={{ px: 2, py: 1 }}>
          <HeaderClock fullWidth />
        </Stack>
        <Divider />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1 }}
        >
          <Typography variant="body2">{t("common.language")}</Typography>
          <LanguageSelector />
        </Stack>
        <Divider />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1 }}
        >
          <Typography variant="body2">{t("common.theme")}</Typography>
          <ColorModeIcon />
        </Stack>
        <Divider />
        <Stack sx={{ p: 2 }}>
          <Button variant="outlined" fullWidth startIcon={<LogoutRounded />} onClick={handleLogout}>
            {t("common.logout")}
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
}
